import { SchedulerPro } from '@bryntum/schedulerpro';
import { Composer } from 'vue-i18n';

import { areRectsOverlapping, isRectLeftOfOtherRect } from '@/helpers/utils/html';
import { NodeName } from '@/repositories/utils/cache';
import { ScheduleStore } from '@/services/store/schedule/store';
import { EventBus } from '@/utils/eventBus';

export function showImmutableHint(
  eventBus: EventBus,
  i18n: Composer,
  type: 'completedFixed' | 'activityAssignment' | 'milestoneAssignment' | 'pauseAssignment',
  overwrite = true,
): void {
  let translationKey = '';
  if (type === 'activityAssignment') {
    translationKey = 'errors.scheduler.activityAssignmentImmutableHint';
  } else if (type === 'milestoneAssignment') {
    translationKey = 'errors.scheduler.milestoneAssignmentImmutableHint';
  } else if (type === 'completedFixed') {
    translationKey = 'errors.scheduler.completedFixedImmutableHint';
  } else if (type === 'pauseAssignment') {
    translationKey = 'errors.scheduler.pauseAssignmentImmutableHint';
  }

  eventBus.emit(
    'new-notification',
    i18n.t(translationKey),
    undefined,
    undefined,
    undefined,
    overwrite,
  );
}

export function targetIsRowExpandButton(event: MouseEvent): boolean {
  return (
    event.target instanceof HTMLElement &&
    Array.from(event.target.classList).some((c) => c.includes('milestone-expand-row-button'))
  );
}

export function cannotShowEventHover({
  isScrolling,
  scheduler,
  scheduleStore,
  entity,
  isPlaceholderOrDryingBreakPlaceholder = false,
  isOverlappingMilestone = false,
  targetIsRowExpandButton = false,
}: {
  isScrolling: boolean;
  scheduler: SchedulerPro | null | undefined;
  scheduleStore: ScheduleStore;
  entity?: NodeName;
  isPlaceholderOrDryingBreakPlaceholder?: boolean;
  isOverlappingMilestone?: boolean;
  targetIsRowExpandButton?: boolean;
}) {
  return Boolean(
    isScrolling ||
      (scheduler?.selectedEvents ?? []).length ||
      scheduleStore.isSidebarOpen ||
      scheduleStore.utils.isDraggingEvent ||
      scheduleStore.utils.isDraggingDependency ||
      scheduleStore.utils.showDragCursor ||
      scheduleStore.utils.disableEventInteractions ||
      (entity && [NodeName.SECTION, NodeName.SECTION_BASEPLAN].includes(entity)) ||
      isPlaceholderOrDryingBreakPlaceholder ||
      isOverlappingMilestone ||
      targetIsRowExpandButton,
  );
}
export function handleRowMouseEnter(resourceId: string) {
  const rowMilestones = document.querySelectorAll(
    `.b-sch-event-parent.b-milestone-wrap[data-resource-id="${resourceId}"]`,
  );

  rowMilestones.forEach((milestone, index) => {
    const currentRect = milestone.getBoundingClientRect();

    let hasOverlap = false;
    let isMostLeftElementOfOverlapGroup = true;

    for (let i = 0; i < rowMilestones.length; i++) {
      if (i === index) continue;

      const sibling = rowMilestones[i];
      const siblingRect = sibling.getBoundingClientRect();

      if (areRectsOverlapping(currentRect, siblingRect)) {
        hasOverlap = true;
        if (!(currentRect.left === siblingRect.left && index < i)) {
          isMostLeftElementOfOverlapGroup =
            isMostLeftElementOfOverlapGroup && isRectLeftOfOtherRect(currentRect, siblingRect);
        }
      }
    }

    if (hasOverlap && isMostLeftElementOfOverlapGroup) {
      const firstChild = milestone.children[0];
      firstChild.classList.add('--with-row-expand-button');
    }
  });
}

export function handleRowMouseLeave(resourceId: string) {
  const rowMilestones = document.querySelectorAll(
    `.b-sch-event-parent[data-resource-id="${resourceId}"]`,
  );

  rowMilestones.forEach((milestone) => {
    const firstChild = milestone.children[0];
    firstChild.classList.remove('--with-row-expand-button');
  });
}

export function showExtendSummaryIndicatorIfNecessary(mouseEvent?: MouseEvent): void {
  if (!mouseEvent) return;

  const elementFromPoint = document.elementFromPoint(mouseEvent.clientX, mouseEvent.clientY);
  if (!elementFromPoint) return;

  let resourceId: string | undefined;
  const hoverElement = document.querySelector('.b-sch-event-hover') as HTMLElement | undefined;

  if (elementFromPoint.classList.contains('section-summary-event')) {
    resourceId =
      (elementFromPoint.parentNode as HTMLElement).getAttribute('data-resource-id') ?? undefined;
  } else if (elementFromPoint.classList.contains('b-sch-summary-row')) {
    resourceId = (elementFromPoint.parentNode as HTMLElement).getAttribute('data-id') ?? undefined;
  } else if (elementFromPoint.classList.contains('b-sch-terminal')) {
    resourceId =
      ((elementFromPoint.parentNode as HTMLElement).parentNode as HTMLElement).getAttribute(
        'data-resource-id',
      ) ?? undefined;
  } else if (elementFromPoint.classList.contains('b-sch-terminal-hover-area')) {
    resourceId =
      (elementFromPoint.parentNode as HTMLElement).getAttribute('data-resource-id') ?? undefined;
  } else if (hoverElement) {
    const eventId = hoverElement.getAttribute('data-event-id');
    if (!eventId) return;
    resourceId = hoverElement.getAttribute('data-resource-id') ?? undefined;
  } else {
    resourceId = (elementFromPoint.parentNode as HTMLElement).getAttribute('data-id') ?? undefined;
  }
  if (!resourceId) return;

  const elements = Array.from(document.querySelectorAll(':has(> .--with-row-expand-button)'));
  elements.forEach(
    (element) =>
      element.getAttribute('data-resource-id') !== resourceId &&
      handleRowMouseLeave(element.getAttribute('data-resource-id') ?? ''),
  );

  if (!resourceId) return;

  handleRowMouseEnter(resourceId);
}
